import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchPublicTenantProfile } from "../api/tenantProfileApi";

const PublicTenantProfile = () => {
  const { tenantId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchPublicTenantProfile(tenantId);
        if (isMounted) {
          setProfile(response.data || null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.message || "Public profile not found or inactive.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (tenantId) {
      loadProfile();
    }
    return () => {
      isMounted = false;
    };
  }, [tenantId]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-[#096dea] border-t-transparent"></div>
          <p className="mt-3 text-sm font-medium text-slate-600">Loading public profile...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md text-center rounded-3xl bg-white p-8 shadow-sm border border-slate-200">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-600">
            ⚠️
          </div>
          <h2 className="mt-4 text-lg font-bold text-slate-900">Profile Unavailable</h2>
          <p className="mt-2 text-sm text-slate-500">{error || "This profile is inactive or does not exist."}</p>
        </div>
      </div>
    );
  }

  const logoSrc =
    profile.logo_data && profile.logo_mime_type
      ? `data:${profile.logo_mime_type};base64,${profile.logo_data}`
      : null;

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-2xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-slate-900/5 p-8 text-center">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={profile.heading || "Company Logo"}
              className="mx-auto h-20 w-20 rounded-2xl border border-slate-200 bg-white object-contain p-2 shadow-sm"
            />
          ) : (
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl border border-slate-200 bg-white text-2xl font-bold text-slate-400 shadow-sm">
              {(profile.heading || "Tenant").charAt(0).toUpperCase()}
            </div>
          )}
          <h1 className="mt-4 text-2xl font-bold text-slate-950">{profile.heading || "Company Profile"}</h1>
          <p className="mt-1 text-xs text-slate-500">Official Tenant Public Profile</p>
        </div>

        <div className="space-y-6 p-8">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">About</h3>
            <p className="mt-2 text-sm text-slate-700 whitespace-pre-line leading-relaxed">
              {profile.description || "No description provided."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicTenantProfile;
